import { useCallback } from 'react';
import { SchedulerDayColumn } from '@lp/features/Scheduler/types';
import { useDayColumns } from '@lp/features/Scheduler/hooks';
import { ScheduleCalendarGrid } from '@lp/features/Scheduler/components';
import { getOMSShiftSetData } from '@lp/features/Scheduler/SchedulerOMS';
import {
  useOMSGridShiftSiblingsMap,
  useOMSOnCallResourcesMap,
} from '@lp/features/Scheduler/SchedulerOMS/components/OMSScheduleGrid/hooks';
import { useOMSSchedulerFiltersStore } from '@lp/core/store/scheduler';
import { OMSResourcesWithShifts, SetOMSShiftData, OMSShiftSelectPayload } from '@/api/apis/resources/schedule';
import { OMSDayGridCell } from './components';

type OMSScheduleGridProps = {
  resourceId: string;
  selectedDate: Date;
  daysInterval: Date[];
  shiftsData: OMSResourcesWithShifts;
  onShiftChange: (resourceId: string, day: string, payload?: SetOMSShiftData) => void;
  isEditable: boolean;
  canResetShifts: boolean;
  isPersonalView?: boolean;
};

export const OMSScheduleGrid = ({
  resourceId,
  isEditable,
  canResetShifts,
  selectedDate,
  daysInterval,
  shiftsData,
  isPersonalView,
  onShiftChange,
}: OMSScheduleGridProps) => {
  const resourcesFilter = useOMSSchedulerFiltersStore.use.resources();
  const myShifts = shiftsData[resourceId]?.days;
  const dayCols = useDayColumns(daysInterval, selectedDate);
  const siblingMap = useOMSGridShiftSiblingsMap(dayCols, myShifts);
  const onCallResourcesMap = useOMSOnCallResourcesMap(dayCols, shiftsData, resourcesFilter);

  const getShiftChangeHandler = useCallback(
    (day: string) => (shiftPayload?: OMSShiftSelectPayload) => {
      if (!shiftPayload) {
        onShiftChange(resourceId, day);
        return;
      }

      const { resource } = shiftsData[resourceId];
      const changeData = getOMSShiftSetData(shiftPayload, resource);

      onShiftChange(resourceId, day, changeData);
    },
    [onShiftChange, resourceId, shiftsData],
  );

  const renderDayCell = useCallback(
    (column: SchedulerDayColumn) => {
      const day = column.key;
      const shift = myShifts?.[day];
      const siblingData = siblingMap.get(day);
      const onCallResources = onCallResourcesMap[day];
      const handler = getShiftChangeHandler(day);

      return (
        <OMSDayGridCell
          key={column.key}
          myId={resourceId}
          column={column}
          shiftData={shift}
          siblingData={siblingData}
          onCallResources={onCallResources}
          isPersonalView={isPersonalView}
          isEditable={isEditable}
          canReset={canResetShifts}
          onShiftChange={handler}
        />
      );
    },
    [
      myShifts,
      siblingMap,
      onCallResourcesMap,
      getShiftChangeHandler,
      resourceId,
      isPersonalView,
      isEditable,
      canResetShifts,
    ],
  );

  return <ScheduleCalendarGrid>{dayCols.map((column) => renderDayCell(column))}</ScheduleCalendarGrid>;
};
